import { PersonFindDto, type PersonFindInterface } from '@zaoblako/shared/modules/client/dtos/person/person-find.dto';
import { JurisdictionTypeEnum } from '@zaoblako/shared/modules/client/enums/jurisdiction-type.enum';
import { ExternalUserFindDto, type ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import type { TenantFindInterface } from '@zaoblako/shared/modules/saas/dtos/tenant-find.dto';
import { SaasDtoKeysEnum } from '@zaoblako/shared/modules/saas/enums/saas-dto-keys.enum';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { ClientDtoKeysEnum } from '@zaoblako/shared/modules/client/enums/client-dto-keys.enum';
import { KktFindDto, type KktFindInterface } from '@zaoblako/shared/modules/finance/dtos/kkt/kkt-find.dto';
import { ActivityFindDto, type ActivityFindInterface } from '@zaoblako/shared/modules/finance/dtos/activity/activity-find.dto';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { BankDetailsFindInterface } from '@zaoblako/shared/modules/client/dtos/bank.details/bank-details-find.dto';

import * as yup from 'yup';

/** Counterparty response body */
export interface ContragentFindInterface extends PersonFindInterface {
  /** Counterparty bank details */
  bankDetails: BankDetailsFindInterface[];
  /** Client this counterparty belongs to */
  client?: ClientFindInterface;
  /** External users this counterparty belongs to */
  externalUsers?: ExternalUserFindInterface[];
  /** Tenant which represented by this counterparty in billing system */
  representedTenant?: TenantFindInterface;
  /** Counterparty activity */
  activity?: ActivityFindInterface;
  /** Counterparty kkts */
  kkts?: KktFindInterface[];
  /** Counterparty jurisdiction */
  jurisdiction?: JurisdictionTypeEnum;
}

/** Counterparty response body - DTO schema */
export const ContragentFindDto = SerdeDtoClass.from<PersonFindInterface, ContragentFindInterface>(
  PersonFindDto,
  ClientDtoKeysEnum.CONTRAGENT_FIND_DTO,
  {
    bankDetails: {
      label: 'Банковские реквизиты',
      nested: ClientDtoKeysEnum.BANK_DETAILS_FIND_DTO,
      serializer: (_, nested) => yup.array(nested),
    },
    client: {
      optional: true,
      label: 'Клиент',
      nested: ClientDtoKeysEnum.CLIENT_FIND_DTO,
      serializer: (_, nested) => nested,
    },
    externalUsers: {
      optional: true,
      label: 'Внешние пользователи',
      nested: ExternalUserFindDto,
      serializer: (_, nested) => yup.array(nested),
    },
    representedTenant: {
      optional: true,
      label: 'Представляет тенант',
      nested: SaasDtoKeysEnum.TENANT_FIND_DTO,
      serializer: (_, nested) => nested,
    },
    activity: {
      optional: true,
      label: 'Вид деятельности',
      nested: ActivityFindDto,
      serializer: (_, nested) => nested,
    },
    kkts: {
      optional: true,
      label: 'Кассы',
      nested: KktFindDto,
      serializer: (_, nested) => yup.array(nested),
    },
    jurisdiction: {
      optional: true,
      label: 'Юрисдикция',
      serializer: yup
        .string()
        .oneOf(Object.values(JurisdictionTypeEnum)),
    },
  },
);
